<script lang="ts" module>
	export interface BottomBarProps {
		combobox?: SearchCombobox | undefined;
		SearchComponent?:
			| undefined
			| Component<{
					combobox: SearchCombobox | undefined;
					onCloseSearch?: (() => void) | undefined;
					onclick?: () => void;
					onkeyup?: () => void;
					class?: string;
					classes?: SearchClasses | undefined;
					forceShowGlobalNav?: boolean | undefined;
			  }>;
		// Extra props forwarded verbatim to the SearchComponent instance.
		searchComponentProps?: { forceShowGlobalNav?: boolean | undefined };
		navigating: boolean;
		siteLinks?: (SiteNavLink & {
			buttonProps?: ButtonProps & {
				title: string;
				dataCy: string;
			};
			linkButtonProps?: LinkButtonProps & {
				title: string;
				dataCy: string;
			};
			children?: import('svelte').Snippet;
			custom?: import('svelte').Snippet;
		})[];
		pathname: string;
		class?: string;
		homeIcon?: import('svelte').Snippet;
		rightLink?: import('svelte').Snippet;
		classes?: {
			siteLinks?: string;
			siteLinksMenu?: string;
			search?: string;
		};
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import type { Component } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import type { ButtonProps } from '../../../../components/Button';
	import { Container } from '../../../../components/Container';
	import { IndeterminateProgressBar } from '../../../../components/IndeterminateProgressBar';
	import type { LinkButtonProps } from '../../../../components/LinkButton/LinkButton.svelte';
	import type { SearchClasses } from '../../../../features/search/components/QuerySearch/Search';
	import type { SearchCombobox } from '../../../../features/search/utils/createSearchCombobox';
	import { BOTTOM_NAVBAR_HEIGHT } from '../../constants/sizing';
	import type { SiteNavLink } from '../../types';
	import { isSiteLinkActive } from '../../utils/isSiteLinkActive/isSiteLinkActive';
	import { SiteLink } from './SiteLink';

	let {
		combobox = undefined,
		SearchComponent = undefined,
		searchComponentProps = {},
		navigating,
		siteLinks = [],
		classes = {},
		homeIcon,
		rightLink,
		pathname,
		...rest
	}: BottomBarProps = $props();

	let dataCyPrefix = $derived('header-bottom-bar');

	const siblingHrefs = $derived(siteLinks.map(({ href }) => href));
</script>

<header
	class={twMerge(
		'bg-gray-0 sticky z-20 hidden w-full items-center border-b border-gray-100 lg:flex',
		BOTTOM_NAVBAR_HEIGHT,
		rest.class
	)}
>
	<Container dataCy={dataCyPrefix} class="relative h-full">
		<div class="flex h-full w-full items-center justify-between bottom-bar">
			<!--
				Symmetric sub-nav (from `lg`/1024px, i.e. wherever this desktop bar is
				shown): the logo column (A) and the site-links column (B) share a 330px
				baseline — the widest site-links group across all routes (Publishing,
				~324px, plus a small buffer). With both sides equal the search fills the
				middle and stays centred on the bar identically on every route. The
				results dropdown centres on this `relative` container.

				330px is a *floor*, not a fixed width, on the site-links side: that
				estimate covers `siteLinks` only, while routes may also render a
				`rightLink` beside them (e.g. Talks' YouTube button). Pinning the column
				to a width narrower than its contents doesn't wrap or shrink them — they
				overflow leftward under the search box, which clips the first tab. So the
				column grows past the baseline when it needs to and the search yields.
			-->
			<div class="flex h-full items-center lg:w-[330px] lg:shrink-0">
				{@render homeIcon?.()}
			</div>
			{#if SearchComponent}
				<SearchComponent {combobox} {...searchComponentProps} class={classes?.search} />
			{/if}
			<div
				class={twMerge(
					'flex h-full items-center lg:w-auto lg:min-w-[330px] lg:shrink-0 lg:justify-end',
					classes?.siteLinks
				)}
			>
				{#each siteLinks as { Icon, label = '', dataCy, href, onClick, items, newTab, show, className, buttonProps, linkButtonProps, children, custom }, i}
					<SiteLink
						{newTab}
						{Icon}
						{buttonProps}
						{linkButtonProps}
						{children}
						{custom}
						{label}
						{href}
						dataCy={`${dataCyPrefix}-${dataCy}`}
						onclick={onClick as () => void}
						{items}
						class={twMerge(
							classNames('cursor-pointer', {
								'ml-4': i > 0,
								'lg-hidden': show === 'mobile-only'
							}),
							className
						)}
						classes={{ menu: classes?.siteLinksMenu }}
						selected={!items && isSiteLinkActive({ pathname, href, siblingHrefs })}
					/>
				{/each}
				{@render rightLink?.()}
			</div>
		</div>
	</Container>

	{#if navigating}
		<IndeterminateProgressBar
			dataCy={dataCyPrefix}
			class="absolute bottom-[-2px] hidden w-full bg-transparent lg:block"
		/>
	{/if}
</header>

<style>
	:global(.bottom-bar > form > input) {
		background-color: white;
	}
</style>
